/**
 * "Put this on my Vision Board" — one tap from a role to a pinned dream.
 *
 * Optimistic, but honest: if the write fails the button goes back to its
 * unpinned state and says so, rather than pretending the dream was kept.
 *
 * The board holds three roles. Three is a plan; twenty is a wish list. When
 * it's full the button says which roles are there and where to swap one out.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pin, PinOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { flushCloudState } from "@/lib/cloudStore";
import {
  CareerDreamLimitError,
  MAX_CAREER_DREAMS,
  isCareerPinned,
  pinCareerToVisionBoard,
  pinnedCareerCount,
  unpinCareerFromVisionBoard,
} from "@/lib/visionBoardDreams";

export default function PinToVisionBoard({
  ciId,
  title,
  family,
  className,
  variant = "outline",
}: {
  ciId: string;
  title: string;
  family?: string | null;
  className?: string;
  variant?: "outline" | "ghost" | "secondary";
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pinned, setPinned] = useState(() => isCareerPinned(ciId));
  const [count, setCount] = useState(() => pinnedCareerCount());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPinned(isCareerPinned(ciId));
    setCount(pinnedCareerCount());
  }, [ciId]);

  const full = !pinned && count >= MAX_CAREER_DREAMS;
  const left = Math.max(0, MAX_CAREER_DREAMS - count);

  /**
   * Stay inside the app: a full page load would throw away the pin that is
   * still waiting in the batched write, so the board would open unpinned.
   */
  const openBoard = useCallback(() => {
    void flushCloudState().finally(() => navigate("/vision-board"));
  }, [navigate]);

  const sayFull = useCallback(() => {
    toast.info(`Your board already holds ${MAX_CAREER_DREAMS} roles`, {
      description: "Take one off to make room for this one — three keeps your next step clear.",
      action: {
        label: "Open board",
        onClick: openBoard,
      },
    });
  }, [openBoard]);

  const toggle = async () => {
    if (full) {
      sayFull();
      return;
    }
    const was = pinned;
    setPinned(!was);
    setBusy(true);
    try {
      if (was) {
        await unpinCareerFromVisionBoard(user?.id ?? null, ciId);
        toast.success(`${title} taken off your Vision Board`);
      } else {
        await pinCareerToVisionBoard(user?.id ?? null, { ciId, title, family });
        toast.success(`${title} is on your Vision Board`, {
          description: "Open it to plan the steps that get you there.",
          action: {
            label: "Open board",
            onClick: openBoard,
          },
        });
      }
    } catch (err) {
      setPinned(was);
      if (err instanceof CareerDreamLimitError) {
        sayFull();
      } else {
        toast.error("That didn't stick", {
          description: "Your board is safe — give it another tap in a moment.",
        });
      }
    } finally {
      setCount(pinnedCareerCount());
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      <Button
        type="button"
        variant={pinned ? "secondary" : variant}
        onClick={() => void toggle()}
        disabled={busy}
        aria-pressed={pinned}
        aria-label={
          pinned
            ? `Remove ${title} from your Vision Board`
            : full
              ? `Your Vision Board already holds ${MAX_CAREER_DREAMS} roles. Remove one to add ${title}.`
              : `Add ${title} to your Vision Board as a dream role`
        }
        className="min-h-12 w-full rounded-2xl"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : pinned ? (
          <PinOff className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Pin className="h-4 w-4" aria-hidden="true" />
        )}
        {pinned
          ? "On your Vision Board"
          : full
            ? "Your board is full"
            : "Make this a Vision Board dream"}
      </Button>
      {pinned ? (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          <Link to="/vision-board" className="underline underline-offset-4 hover:text-foreground">
            Open your Vision Board
          </Link>{" "}
          to add the steps.
        </p>
      ) : full ? (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Three roles at a time keeps your focus sharp.{" "}
          <Link to="/vision-board" className="underline underline-offset-4 hover:text-foreground">
            Swap one out
          </Link>
          .
        </p>
      ) : (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          {left === MAX_CAREER_DREAMS
            ? `You can keep ${MAX_CAREER_DREAMS} roles on your board.`
            : `${left} ${left === 1 ? "space" : "spaces"} left on your board.`}
        </p>
      )}
    </div>
  );
}
